// Taşeron portalı — Personelim (api-contract §13.6 `GET /supplier/contractor/personnel`).
// Supplier **kendi** taşeron firmasının personelini görür: aynı contractor'a bağlı **aktive
// `SupplierUser`'lar + bekleyen davetler** (§13.3 ile aynı `ContractorPersonnelDto` şekli —
// `kind`/`status`/`accessLevel`). Bağ yoksa (404) / erişim yoksa (403) → nazik durum ekranı.
//
// Aktive personelin adı (adsızsa iletişimi) bir düğmedir ve ortak kişi çekmecesini açar
// (`PersonnelDrawer`, GL5); davet satırı düz metin kalır. Kişi başka bir ekrandan (pano "eksik
// belge", eski `/personnel/:uuid` adresleri) router state'iyle de açılır (`useConsumeOpenPersonnel`);
// listede olmayan kişi için kapatılabilir bir "bulunamadı" bildirimi gösterilir.
//
// Satır ⋮ menüsü kind'e göre (§13.6; oturumdaki kişi HARİÇ — kendi satırında ⋮ yok, sebep
// çekmecede yazar):
//   - Aktive personel (kind = Personnel): Düzenle (`isActive` bilinmiyorsa yok; tahmin edilmez) +
//     Firmadan çıkar. Etkinleştirilmemiş kişi (status = NoLogin, aktif) ayrıca: bağlantı yoksa
//     Aktivasyon bağlantısı gönder; bekleyen bağlantı varsa Bağlantıyı tekrar gönder / iptal et
//     (iptal kişi satırını TUTAR, yalnız `pendingLink` temizlenir).
//   - Düz bekleyen davet (kind = Invitation): tekrar gönder + iptal. İptal edilen düz davet
//     listeden KALKAR (Revoked satırlar artık listelenmez, GL6 §4.6).
// Yeni personel üstteki "Personel oluştur" ile (`PersonnelCreateSheet`); bağlantı gönder/tekrar
// gönder `PersonnelLinkModal` ile. Satırı Firmadan çıkar ve düz davet iptali kaldırır → odak
// "Personel oluştur"a iner (`useRowRemovalFocus`); başarısız çıkarma/iptal de listeyi tazeler
// (bayat satır kalmaz). Kişinin bağlantı iptali satırı TUTAR.
//
// **Düzenleme (#29).** Oturum (`usePersonnelEditor` + `usePersonnelClient`) BU sayfada yaşar ve
// çekmeceye geçer: ⋮ "Düzenle" `begin` + çekmeceyi açmayı tek olayda yapar → çekmecenin ilk karesi
// zaten düzenleme kipidir (görünüm karesi, belge isteği yok). Ad düğmesi / gezinme görünüm kipinde
// açar; oradan çekmecedeki "Düzenle" aynı oturuma girer. Başarı bildirimi çekmecededir.

import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  type ContractorPersonnelDto,
  EmptyState,
  PageHeader,
  PendingLinkLine,
  PersonnelCreateSheet,
  PersonnelLinkModal,
  RowActionsMenu,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  Tag,
  currentCulture,
  personLabel,
  supplierContractorApi,
  toEditTarget,
  useAuthStore,
  useConsumeOpenPersonnel,
  usePersonnelEditor,
  usePersonnelLinkActions,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContractorStateNotice } from '../components/ContractorStateNotice';
import { PersonnelDrawer } from '../components/personnel/PersonnelDrawer';
import {
  accessLevelColor,
  personnelStatusColor,
  personnelStatusLabelKey,
} from '../components/status/tone';
import { formatDate } from '../lib/format';
import { usePersonnelActivationClient } from '../lib/personnelActivationClient';
import { usePersonnelClient } from '../lib/personnelClient';
import { supplierContractorKeys } from '../lib/supplierContractorKeys';
import { useTableDensity } from '../lib/uiPrefs';
import { useReturnFocus } from '../lib/useReturnFocus';

/** İşlemler sütunu hep render edilir (#13/#26 ⋮ öğeleri ekleyince yerleşim kaymasın). */
const COLUMN_COUNT = 6;

/** İlgili tarih — davette davet anı; etkinleştirilmemiş kişide son bağlantının gönderimi, yoksa aktivasyon. */
function relevantDate(person: ContractorPersonnelDto): string | null {
  if (person.kind === 'Invitation') {
    return person.invitedAt;
  }
  return person.pendingLink?.sentAt ?? person.activatedAt;
}

export function PersonnelPage() {
  const { t } = useTranslation();
  const culture = currentCulture();
  const queryClient = useQueryClient();
  const companyId = useAuthStore((s) => s.principal?.companyId ?? null);
  const principalUuid = useAuthStore((s) => s.principal?.uuid ?? null);

  const [createOpen, setCreateOpen] = useState(false);
  const activation = usePersonnelActivationClient(companyId);
  const [toRemove, setToRemove] = useState<ContractorPersonnelDto | null>(null);
  // Çekmecesi açık kişi (uuid) ve odağın döneceği düğüm; gezinme isteği listede olmayan kişiyi
  // soruyorsa `notFound` (kapatılabilir bildirim).
  const [drawerUuid, setDrawerUuid] = useState<string | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const [notFound, setNotFound] = useState(false);
  // Oluştur sayfası kapanınca odak "Personel oluştur"a döner (`Sheet.restoreFocusRef`; `Sheet`
  // bir `Dialog.Trigger` taşımadığından Radix hiçbir şeyi geri yüklemez, bkz. `useReturnFocus`).
  const createTrigger = useReturnFocus();
  // Satırların ad düğmeleri (yalnız aktive personel) ve ⋮ tetikleyicileri — menüden açılan
  // pencerenin odak dönüş zinciri: ad → ⋮ → "Personel oluştur".
  const nameButtons = useRef(new Map<string, HTMLButtonElement>());
  const triggers = useRef(new Map<string, HTMLButtonElement>());

  const editor = usePersonnelEditor({
    client: usePersonnelClient(companyId),
    onCloseDrawer: () => setDrawerUuid(null),
  });

  const query = useQuery({
    queryKey: supplierContractorKeys.personnel(companyId),
    queryFn: () => supplierContractorApi.listSupplierContractorPersonnel(),
  });

  const rows = query.data ?? [];
  const rowKeys = useMemo(() => rows.map((p) => p.uuid), [rows]);
  const drawerPerson = rows.find((p) => p.uuid === drawerUuid && p.kind === 'Personnel') ?? null;

  const rowsRef = useRef(rows);
  useEffect(() => {
    rowsRef.current = rows;
  });
  const openFromNavigation = useCallback((uuid: string) => {
    const hit = rowsRef.current.find((p) => p.uuid.toLowerCase() === uuid.toLowerCase());
    if (!hit) {
      setNotFound(true);
      return;
    }
    setNotFound(false); // sonraki istek kişisini buldu → önceki "bulunamadı" bildirimi bayat
    if (hit.kind !== 'Personnel') {
      return; // davet: düz metin satır, açılacak çekmece yok
    }
    openerRef.current = nameButtons.current.get(hit.uuid) ?? null; // odak kişinin adına döner
    setDrawerUuid(hit.uuid);
  }, []);
  // Arka plan tazelemesi sürerken önbellekteki bayat listeye bakılmaz (yeni aktive kişi "bulunamadı" olmasın).
  useConsumeOpenPersonnel({
    ready: query.isSuccess && !query.isFetching,
    onOpen: openFromNavigation,
  });

  // Menü öğesinin kendisi menü kapanınca DOM'dan kalkar; dönüş hedefi satırın adı, yoksa ⋮'si.
  function rowOpener(person: ContractorPersonnelDto) {
    return (
      nameButtons.current.get(person.uuid) ??
      triggers.current.get(person.uuid) ??
      createTrigger.ref.current
    );
  }
  const links = usePersonnelLinkActions({
    activation,
    rowKeys,
    stamps: { dataUpdatedAt: query.dataUpdatedAt, errorUpdatedAt: query.errorUpdatedAt },
    addButtonRef: createTrigger.ref,
    dialogOpen: toRemove !== null,
    otherSurfaceOpen: createOpen || drawerPerson !== null,
    rowOpener,
    triggers,
    honorPersonalDataMask: false,
  });
  const { focus } = links;

  // `personnelByUuid` o kişinin belgelerinin önekidir (`supplierContractorKeys.ts`) — çıkarılan
  // kişinin belge önbelleği de düşer; istenen aşırı-invalidasyon.
  function invalidatePersonnel(userUuid: string) {
    queryClient.invalidateQueries({ queryKey: supplierContractorKeys.personnel(companyId) });
    queryClient.invalidateQueries({
      queryKey: supplierContractorKeys.personnelByUuid(companyId, userUuid),
    });
  }

  const removeMutation = useMutation({
    mutationFn: (userUuid: string) =>
      supplierContractorApi.deleteSupplierContractorPersonnel(userUuid),
    onSuccess: (_data, userUuid) => {
      invalidatePersonnel(userUuid);
      focus.removed(userUuid);
      setToRemove(null);
    },
    // #25 bulgusu: başarısız çıkarma (404 zaten gitmiş, 409) bayat satırı ekranda bırakıyordu.
    onError: (_error, userUuid) => {
      invalidatePersonnel(userUuid);
      focus.removeFailed(userUuid);
    },
  });

  const [density, setDensity] = useTableDensity();

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb items={[{ label: t('personnel.title') }]} />
      <PageHeader
        title={t('personnel.title')}
        helperText={t('personnel.subtitle')}
        actions={
          /* Oluştur butonu yalnız firma bağı çözüldüğünde (liste başarıyla geldiğinde) */
          query.isSuccess ? (
            <Button ref={createTrigger.ref} onClick={() => setCreateOpen(true)}>
              <UserPlus className="h-4 w-4" aria-hidden="true" />
              {t('personnel.createAction')}
            </Button>
          ) : undefined
        }
      />

      {notFound ? (
        // `Alert`in kapat düğmesi yalnız Carbon derlemesinde görünür → varsayılan temada kendi
        // ghost düğmemiz (`onDismiss` verilmez, çift düğme olmasın). Düğme kalkınca odak
        // `<body>`ye düşmesin → "Personel oluştur".
        <Alert tone="info">
          <div className="flex items-center justify-between gap-2">
            <span>{t('personnel.detail.notFound.body')}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setNotFound(false);
                createTrigger.ref.current?.focus();
              }}
            >
              {t('common.close')}
            </Button>
          </div>
        </Alert>
      ) : null}

      {query.isError ? (
        <ContractorStateNotice error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <Card className="carbon:p-0">
          <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
            {/* Uç noktada arama/sayfalama YOK (§13.6, dizi döner) → araç çubuğu yalnız özet sayı. */}
            <TableToolbar
              summary={query.data ? t('personnel.list.count', { count: rows.length }) : undefined}
              density={density}
              onDensityChange={setDensity}
              densityLabel={t('common.compactRows')}
            />
            {/* `relative`: sütun başlığındaki `sr-only` metnin kapsayıcı bloğu tablo olsun (araç
                listesiyle aynı). İletişim sütunu sıkışmayı emer (≥sm `w-full` + `max-w-0`: içerik
                genişliği tabloya katkı vermez, kalan genişliği alır ve kırpılır); <sm tablo zaten
                kendi kabında kayar → sabit 12rem. */}
            <Table caption={t('personnel.title')} density={density} className="relative">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('personnel.col.name')}</TableHead>
                  <TableHead
                    className="w-full truncate sm:max-w-0"
                    title={t('personnel.col.contact')}
                  >
                    {t('personnel.col.contact')}
                  </TableHead>
                  <TableHead>{t('personnel.col.status')}</TableHead>
                  <TableHead>{t('personnel.col.access')}</TableHead>
                  <TableHead>{t('personnel.col.date')}</TableHead>
                  <TableHead className="w-12">
                    <span className="sr-only">{t('common.actions')}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody aria-busy={query.isPending || undefined}>
                {query.isPending ? (
                  <TableSkeletonRows columns={COLUMN_COUNT} />
                ) : rows.length === 0 ? (
                  <TableMessageRow colSpan={COLUMN_COUNT}>
                    <EmptyState icon={Users} title={t('personnel.empty')} />
                  </TableMessageRow>
                ) : (
                  rows.map((person) => {
                    // Davet satırı yalnız adı gösterir (iletişim kendi sütununda); aktive personelin
                    // düğmesi, `title`, ⋮ etiketi ve onaylar `personLabel`'ı (ad, yoksa iletişim) okur.
                    const name = personLabel({
                      firstName: person.firstName,
                      lastName: person.lastName,
                    });
                    const label = personLabel(person);
                    const isSelf =
                      principalUuid !== null &&
                      principalUuid.toLowerCase() === person.uuid.toLowerCase();
                    return (
                      <TableRow key={person.uuid}>
                        <TableCell truncate={false}>
                          {/* Satırın birincil "aç" eylemi: aktive personelde ad (adsızsa iletişim) ortak
                              kişi çekmecesini açan bir düğme; davette (kind = Invitation) açılacak
                              çekmece yok, ad düz metin kalır. Ne ad ne iletişim varsa soluk "—"
                              (boş bir odak hedefi olmasın). */}
                          <div className="max-w-[8rem]">
                            {person.kind === 'Personnel' && label ? (
                              <button
                                type="button"
                                ref={(el) => {
                                  if (el) {
                                    nameButtons.current.set(person.uuid, el);
                                  } else {
                                    nameButtons.current.delete(person.uuid);
                                  }
                                }}
                                title={label}
                                onClick={(e) => {
                                  openerRef.current = e.currentTarget;
                                  setDrawerUuid(person.uuid);
                                }}
                                className="block w-full truncate text-left font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              >
                                {label}
                              </button>
                            ) : person.kind === 'Invitation' && name ? (
                              <span className="block truncate font-medium text-fg" title={label}>
                                {name}
                              </span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell truncate={false} className="text-muted sm:max-w-0">
                          <span
                            className="block w-48 truncate sm:w-auto"
                            title={
                              person.pendingLink?.emailOrPhone ?? person.emailOrPhone ?? undefined
                            }
                          >
                            {person.pendingLink?.emailOrPhone ?? person.emailOrPhone ?? '—'}
                          </span>
                          {person.pendingLink ? (
                            <PendingLinkLine link={person.pendingLink} />
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Tag color={personnelStatusColor(person.status)}>
                            {t(personnelStatusLabelKey(person))}
                          </Tag>
                        </TableCell>
                        <TableCell>
                          <Tag color={accessLevelColor(person.accessLevel)}>
                            {t(`personnel.access.${person.accessLevel.toLowerCase()}`)}
                          </Tag>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-muted tnum">
                          {formatDate(relevantDate(person), culture)}
                        </TableCell>
                        <TableCell className="w-12 px-2">
                          {/* Davet → tekrar gönder + iptal. Aktive personel → Düzenle (yalnız `isActive`
                              boolean iken; tahmin edilmez — `toEditTarget` aynı koşulu bağımsızca
                              tekrar denetler, hedef yoksa çekmece açılmaz) + bağlantı eylemleri
                              (etkinleştirilmemişse) + Firmadan çıkar. Oturumdaki kişinin kendi satırında ⋮
                              HİÇ yok (boş menü, menüsüzden kötü); sunucu 409
                              PERSONNEL_SELF_TARGET_FORBIDDEN yine yedek. */}
                          {person.kind === 'Invitation' || !isSelf ? (
                            <RowActionsMenu
                              ariaLabel={t('personnel.action.openMenu', { name: label })}
                              triggerRef={(el) => {
                                if (el) {
                                  triggers.current.set(person.uuid, el);
                                } else {
                                  triggers.current.delete(person.uuid);
                                }
                              }}
                              items={
                                person.kind === 'Invitation'
                                  ? links.linkItems(person)
                                  : [
                                      ...(typeof person.isActive === 'boolean'
                                        ? [
                                            {
                                              key: 'edit',
                                              label: t('personnelForm.edit.action'),
                                              // `begin` + açılış TEK olayda → ilk kare düzenleme kipi.
                                              onSelect: () => {
                                                openerRef.current = rowOpener(person);
                                                const target = toEditTarget(person);
                                                if (target) {
                                                  editor.begin(target);
                                                  setDrawerUuid(person.uuid);
                                                }
                                              },
                                            },
                                          ]
                                        : []),
                                      ...links.linkItems(person),
                                      {
                                        key: 'remove',
                                        label: t('personnel.detail.remove.action'),
                                        destructive: true,
                                        onSelect: () => {
                                          removeMutation.reset();
                                          links.rememberRow(person);
                                          setToRemove(person);
                                        },
                                      },
                                    ]
                              }
                            />
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <PersonnelCreateSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        client={activation}
        restoreFocusRef={createTrigger.ref}
      />

      <PersonnelLinkModal {...links.linkModalProps} />

      <ConfirmDialog
        open={toRemove !== null}
        onOpenChange={(open) => !open && setToRemove(null)}
        title={t('personnel.detail.remove.title')}
        // Hesabını hiç etkinleştirmemiş (NoLogin) kişi yeniden eklenirse YENİ kayıt olur → belgeleri geri gelmez.
        body={t(
          toRemove?.status === 'NoLogin'
            ? 'personnel.detail.remove.bodyNotActivated'
            : 'personnel.detail.remove.body',
          { name: toRemove ? personLabel(toRemove) : '' },
        )}
        confirmLabel={t('personnel.detail.remove.action')}
        restoreFocusRef={focus.menuRestoreRef}
        loading={removeMutation.isPending}
        error={removeMutation.isError ? removeMutation.error : undefined}
        onConfirm={() => toRemove && removeMutation.mutate(toRemove.uuid)}
      />

      <ConfirmDialog {...links.cancelDialogProps} />

      <PersonnelDrawer
        userUuid={drawerUuid}
        onOpenChange={(open) => !open && setDrawerUuid(null)}
        onTargetGone={() => {
          openerRef.current = createTrigger.ref.current;
        }}
        restoreFocusRef={openerRef}
        editor={editor}
      />
    </div>
  );
}
